import { useState } from "react";
import type { CallMe } from "@bundle/core";
import { Button, IconButton, Popover, Stack } from "../../primitives/index.ts";
import { useT } from "../../i18n/I18nProvider.tsx";

export type CallControlsProps = {
  me: CallMe;
  /** Screen sharing is offered (web/desktop browsers that support it). */
  canShareScreen: boolean;
  size?: "sm" | "md" | "lg";
  /** "Leave" with its label (call pane) or icon-only (sidebar bar, pill). */
  leaveLabel?: boolean;
  onMute: () => void;
  onVideo: () => void;
  onScreen: () => void;
  onHand: () => void;
  /** Reactions in the call (Slack huddle reactions): the quick choices; the button is left out without `onReact`. */
  reactions?: readonly string[];
  onReact?: (emoji: string) => void;
  onSettings: () => void;
  onLeave: () => void;
  testID: string;
};

/** Mute, video, screen share, raise hand, reactions, settings and leave. A filled button = that thing is on. */
export function CallControls(p: CallControlsProps) {
  const t = useT();
  const size = p.size ?? "md";
  const [reacting, setReacting] = useState(false);
  const onReact = p.onReact;
  return (
    <Stack direction="horizontal" align="center" gap="1" testID={p.testID}>
      <IconButton
        icon={p.me.muted ? "micOff" : "mic"}
        variant={p.me.muted ? "filled" : "ghost"}
        size={size}
        accessibilityLabel={p.me.muted ? t("huddle.unmute") : t("huddle.mute")}
        onPress={p.onMute}
        testID={`${p.testID}-mute`}
      />
      <IconButton
        icon={p.me.video ? "video" : "videoOff"}
        variant={p.me.video ? "filled" : "ghost"}
        size={size}
        accessibilityLabel={p.me.video ? t("huddle.videoOff") : t("huddle.videoOn")}
        onPress={p.onVideo}
        testID={`${p.testID}-video`}
      />
      {p.canShareScreen ? (
        <IconButton
          icon={p.me.screen ? "screenShareOff" : "screenShare"}
          variant={p.me.screen ? "filled" : "ghost"}
          size={size}
          accessibilityLabel={p.me.screen ? t("huddle.stopSharing") : t("huddle.shareScreen")}
          onPress={p.onScreen}
          testID={`${p.testID}-screen`}
        />
      ) : null}
      <IconButton
        icon="hand"
        variant={p.me.hand ? "filled" : "ghost"}
        size={size}
        accessibilityLabel={p.me.hand ? t("huddle.lowerHand") : t("huddle.raiseHand")}
        onPress={p.onHand}
        testID={`${p.testID}-hand`}
      />
      {onReact === undefined || p.reactions === undefined ? null : (
        <Popover
          open={reacting}
          onClose={() => setReacting(false)}
          side="top"
          align="center"
          anchor={<IconButton icon="emoji" variant={reacting ? "filled" : "ghost"} size={size} accessibilityLabel={t("huddle.react")} onPress={() => setReacting(!reacting)} testID={`${p.testID}-react`} />}
        >
          <Stack direction="horizontal" gap="0.5" padding="1" testID={`${p.testID}-reactions`}>
            {p.reactions.map((emoji, i) => (
              <Button key={emoji} label={emoji} variant="ghost" size="sm" onPress={() => onReact(emoji)} testID={`${p.testID}-reaction-${i}`} />
            ))}
          </Stack>
        </Popover>
      )}
      <IconButton icon="settings" size={size} accessibilityLabel={t("huddle.settings")} onPress={p.onSettings} testID={`${p.testID}-settings`} />
      {p.leaveLabel === true ? (
        <Button label={t("huddle.leave")} icon="leave" variant="destructive" size={size === "lg" ? "md" : "sm"} onPress={p.onLeave} testID={`${p.testID}-leave`} />
      ) : (
        <IconButton icon="leave" size={size} accessibilityLabel={t("huddle.leave")} onPress={p.onLeave} testID={`${p.testID}-leave`} />
      )}
    </Stack>
  );
}
